import { useEffect, useState } from "react";
import { EsqueletoDashboard } from "../../components/Esqueleto";
import { EstadoVacio } from "../../components/EstadoVacio";
import { GraficoBarras } from "../../components/GraficoBarras";
import { GraficoDona } from "../../components/GraficoDona";
import { api } from "../../lib/apiClient";
import { COLOR_POR_PROBABILIDAD, COLOR_POR_RIESGO, colorParaUsuario, resolverColorSemantico } from "../../lib/colorSemantico";
import { SeccionDashboard, TarjetaGrafico, TarjetaKPI } from "./ComunesDashboard";

interface Etiquetada {
  etiqueta: string;
  total: number;
  color?: string | null;
}

interface EtiquetadaConId extends Etiquetada {
  id: string;
}

interface EtapaTiempoPromedio {
  etapa_id: string;
  etapa_nombre: string;
  proceso_nombre: string;
  promedio_dias: number;
  tiempo_objetivo_dias: number | null;
  cantidad: number;
}

interface DatosJudicial {
  expedientes: { judiciales: number; extrajudiciales: number; archivados: number; urgentes: number };
  caducidades: number;
  audienciasProximas: number;
  escritosPendientes: number;
  tiempoPromedioResolucionDias: number | null;
  productividad: {
    actuacionesEsteMes: number;
    actuacionesPorTipo: Etiquetada[];
    rankingAbogados: EtiquetadaConId[] | null;
  };
  graficos: {
    porMateria: Etiquetada[];
    porSubmateria: Etiquetada[];
    porFuero: Etiquetada[];
    porOrganismo: Etiquetada[];
    porAbogado: EtiquetadaConId[] | null;
    porRiesgo: Etiquetada[] | null;
    porProbabilidad: Etiquetada[] | null;
  };
  procesos: {
    tiempoPromedioPorEtapa: EtapaTiempoPromedio[];
    cuelloBotella: EtapaTiempoPromedio | null;
    tiempoTotalPromedioDias: number | null;
    expedientesDemorados: number;
  };
}

// 100% jurídico: sin indicadores empresariales/financieros (eso vive en el
// Dashboard CEO/Ejecutivo).
export function DashboardJudicialPage() {
  const [datos, setDatos] = useState<DatosJudicial | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<DatosJudicial>("/dashboard/judicial")
      .then(setDatos)
      .catch(() => setError("No se pudo cargar el panel."));
  }, []);

  if (error) return <EstadoVacio titulo="Error" descripcion={error} />;
  if (!datos) return <EsqueletoDashboard />;

  const g = datos.graficos;
  const hayRiesgoOProbabilidad = g.porRiesgo || g.porProbabilidad;

  return (
    <div>
      <SeccionDashboard titulo="Expedientes">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <TarjetaKPI
            etiqueta="Judiciales"
            valor={datos.expedientes.judiciales}
            icono="balanza"
            tono="info"
            destacado
            className="sm:col-span-2"
          />
          <TarjetaKPI etiqueta="Extrajudiciales" valor={datos.expedientes.extrajudiciales} icono="expedientes" tono="neutro" />
          <TarjetaKPI etiqueta="Archivados" valor={datos.expedientes.archivados} icono="expedientes" tono="neutro" />
          <TarjetaKPI
            etiqueta="Urgentes"
            valor={datos.expedientes.urgentes}
            subtitulo="Prioridad Alta"
            icono="reloj-alerta"
            tono="alerta"
          />
        </div>
      </SeccionDashboard>

      <SeccionDashboard titulo="Agenda y actividad">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <TarjetaKPI etiqueta="Caducidades pendientes" valor={datos.caducidades} icono="reloj-alerta" tono="advertencia" />
          <TarjetaKPI etiqueta="Audiencias próximas" valor={datos.audienciasProximas} subtitulo="7 días" icono="agenda" tono="info" />
          <TarjetaKPI etiqueta="Escritos pendientes" valor={datos.escritosPendientes} icono="documentos" tono="advertencia" />
          <TarjetaKPI
            etiqueta="Tiempo promedio de resolución"
            valor={datos.tiempoPromedioResolucionDias != null ? `${Math.round(datos.tiempoPromedioResolucionDias)} días` : "—"}
            icono="reloj-alerta"
            tono="neutro"
          />
        </div>
      </SeccionDashboard>

      {hayRiesgoOProbabilidad && (
        <SeccionDashboard titulo="Riesgo y probabilidad de éxito">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {g.porRiesgo && (
              <TarjetaGrafico titulo="Por Nivel de Riesgo" icono="alerta">
                <GraficoDona
                  datos={g.porRiesgo.map((r) => ({
                    etiqueta: r.etiqueta,
                    valor: r.total,
                    color: resolverColorSemantico(COLOR_POR_RIESGO[r.etiqueta]).hex,
                  }))}
                />
              </TarjetaGrafico>
            )}
            {g.porProbabilidad && (
              <TarjetaGrafico titulo="Por Probabilidad de Éxito" icono="alerta">
                <GraficoDona
                  datos={g.porProbabilidad.map((p) => ({
                    etiqueta: p.etiqueta,
                    valor: p.total,
                    color: resolverColorSemantico(COLOR_POR_PROBABILIDAD[p.etiqueta]).hex,
                  }))}
                />
              </TarjetaGrafico>
            )}
          </div>
        </SeccionDashboard>
      )}

      <SeccionDashboard titulo="Productividad jurídica">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <TarjetaKPI etiqueta="Actuaciones este mes" valor={datos.productividad.actuacionesEsteMes} icono="actuaciones" tono="exito" />
          {datos.productividad.rankingAbogados && (
            <TarjetaGrafico titulo="Ranking de abogados (mes)" icono="usuarios">
              {datos.productividad.rankingAbogados.length === 0 ? (
                <EstadoVacio titulo="Sin actuaciones este mes" />
              ) : (
                <GraficoBarras
                  datos={datos.productividad.rankingAbogados.map((r) => ({
                    etiqueta: r.etiqueta,
                    valor: r.total,
                    color: resolverColorSemantico(colorParaUsuario(r.id)).barra,
                  }))}
                />
              )}
            </TarjetaGrafico>
          )}
          {/* Desglose del mismo total de "Actuaciones este mes" por tipo —
              mismo universo (mes actual, activas, mismo filtro de área), no
              una métrica con otro horizonte temporal. */}
          <TarjetaGrafico titulo="Actuaciones por tipo (mes)" icono="actuaciones">
            {datos.productividad.actuacionesPorTipo.length === 0 ? (
              <EstadoVacio titulo="Sin actuaciones este mes" />
            ) : (
              <GraficoBarras
                datos={datos.productividad.actuacionesPorTipo.map((t) => ({ etiqueta: t.etiqueta, valor: t.total }))}
              />
            )}
          </TarjetaGrafico>
        </div>
      </SeccionDashboard>

      {datos.procesos.tiempoPromedioPorEtapa.length > 0 && (
        <SeccionDashboard titulo="Procesos de Trabajo">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <TarjetaKPI
              etiqueta="Tiempo total promedio del proceso"
              valor={
                datos.procesos.tiempoTotalPromedioDias != null
                  ? `${Math.round(datos.procesos.tiempoTotalPromedioDias)} días`
                  : "—"
              }
              icono="kanban"
              tono="neutro"
            />
            <TarjetaKPI
              etiqueta="Expedientes demorados"
              subtitulo="Etapa vencida"
              valor={datos.procesos.expedientesDemorados}
              icono="reloj-alerta"
              tono={datos.procesos.expedientesDemorados > 0 ? "alerta" : "neutro"}
            />
            <TarjetaKPI
              etiqueta="Cuello de botella"
              subtitulo={
                datos.procesos.cuelloBotella
                  ? `${datos.procesos.cuelloBotella.proceso_nombre} — ${datos.procesos.cuelloBotella.etapa_nombre}`
                  : undefined
              }
              valor={
                datos.procesos.cuelloBotella
                  ? `${Math.round(datos.procesos.cuelloBotella.promedio_dias)} días`
                  : "—"
              }
              icono="kanban"
              tono="advertencia"
            />
          </div>
          <TarjetaGrafico titulo="Tiempo promedio por etapa (expedientes activos)" icono="kanban" className="mt-6">
            <GraficoBarras
              datos={datos.procesos.tiempoPromedioPorEtapa.map((e) => ({
                etiqueta: `${e.proceso_nombre} · ${e.etapa_nombre}`,
                valor: Math.round(e.promedio_dias),
              }))}
            />
          </TarjetaGrafico>
        </SeccionDashboard>
      )}

      <SeccionDashboard titulo="Información Estratégica">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <TarjetaGrafico titulo="Por Materia" icono="expedientes">
            <GraficoBarras
              datos={g.porMateria.map((m) => ({ etiqueta: m.etiqueta, valor: m.total, color: resolverColorSemantico(m.color).barra }))}
            />
          </TarjetaGrafico>
          <TarjetaGrafico titulo="Por Submateria" icono="expedientes">
            <GraficoBarras
              datos={g.porSubmateria.map((m) => ({ etiqueta: m.etiqueta, valor: m.total, color: resolverColorSemantico(m.color).barra }))}
            />
          </TarjetaGrafico>
          <TarjetaGrafico titulo="Por Fuero" icono="balanza">
            <GraficoBarras
              datos={g.porFuero.map((m) => ({ etiqueta: m.etiqueta, valor: m.total, color: resolverColorSemantico(m.color).barra }))}
            />
          </TarjetaGrafico>
          <TarjetaGrafico titulo="Por Organismo" icono="balanza">
            <GraficoBarras
              datos={g.porOrganismo.map((m) => ({ etiqueta: m.etiqueta, valor: m.total, color: resolverColorSemantico(m.color).barra }))}
            />
          </TarjetaGrafico>
          {g.porAbogado && (
            <TarjetaGrafico titulo="Por Abogado" icono="usuarios">
              <GraficoBarras
                datos={g.porAbogado.map((m) => ({ etiqueta: m.etiqueta, valor: m.total, color: resolverColorSemantico(colorParaUsuario(m.id)).barra }))}
              />
            </TarjetaGrafico>
          )}
        </div>
      </SeccionDashboard>
    </div>
  );
}
